import { ProductInboundTransactionDoc, ProductTransactionStatus } from '@gooduncles/gu-app-schema';
import { ColDef, ITooltipParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';

import {
  cellStyleForDate,
  formatNumberForAgCell,
  getCellStyleForTransactionStatus,
  getUnitCellStyle,
  inboundReferenceCollectionFormatter,
  productTransactionStatusFormatter,
  stringDateFormatter,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellInventory from 'src/atomic-components/atoms/AgCell/AgCellInventory';

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface InventoryInboundHistoryTableProps {
  rowData: ProductInboundTransactionDoc[];
  hideReferenceCollection?: boolean;
}

const InventoryInboundHistoryTable: FC<InventoryInboundHistoryTableProps> = ({ rowData, hideReferenceCollection }) => {
  const columnDefs: ColDef<ProductInboundTransactionDoc>[] = useMemo(
    () => [
      {
        headerName: 'Id',
        field: '_id',
        width: 80,
        hide: true,
      },
      {
        headerName: 'inventoryId',
        field: 'inventoryId',
        width: 80,
        hide: true,
      },
      {
        headerName: '입고일',
        field: 'requestDate',
        width: 120,
        valueFormatter: stringDateFormatter,
        cellStyle: cellStyleForDate,
      },
      {
        headerName: '입고 경로',
        field: 'referenceCollection',
        width: 120,
        hide: hideReferenceCollection,
        valueFormatter: inboundReferenceCollectionFormatter,
        filterParams: {
          valueFormatter: inboundReferenceCollectionFormatter,
        },
      },
      {
        headerName: '재고명',
        field: 'inventoryName',
        width: 240,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => ({
          inventoryId: params.data?.inventoryId,
          field: 'inventoryName',
        }),
      },
      {
        headerName: '변경량',
        field: 'requestValue',
        width: 100,
        valueGetter: (params) => {
          const transaction = params.data;
          if (!transaction) return null;
          return transaction.requestValue * transaction.inventoryUnitRatio;
        },
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        cellRenderer: (params: any) => {
          const formattedValue = params.value === Infinity ? '∞' : formatNumber(params.value);
          const status: ProductTransactionStatus = params.data?.status;
          return <span className={`stock-cell inbound ${status}`}>{formattedValue}</span>;
        },
      },
      {
        headerName: '단위',
        field: 'inventoryUnit',
        width: 88,
        cellStyle: getUnitCellStyle,
      },
      {
        headerName: '비율',
        field: 'inventoryUnitRatio',
        width: 80,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
      },
      {
        headerName: '요청 값',
        field: 'requestValue',
        width: 96,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
      },
      {
        headerName: '상태',
        field: 'status',
        width: 80,
        valueFormatter: productTransactionStatusFormatter,
        cellStyle: getCellStyleForTransactionStatus,
        filterParams: {
          valueFormatter: productTransactionStatusFormatter,
        },
        tooltipValueGetter: (params: ITooltipParams<ProductInboundTransactionDoc, ProductTransactionStatus>) => {
          if (
            params.value &&
            [ProductTransactionStatus.REJECTED, ProductTransactionStatus.FAILED].includes(params.value)
          ) {
            return params.data?.error;
          }
          return null;
        },
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ],
    [hideReferenceCollection]
  );

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        tooltipShowDelay={500}
      />
    </div>
  );
};

export default InventoryInboundHistoryTable;
